import React, { useContext, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  Stack,
  Typography,
  Chip,
  Button,
  Menu,
  MenuItem,
  Divider,
  Skeleton,
  CircularProgress
} from '@mui/material';
import {
  AddCircleOutline,
  Publish,
  EventAvailable,
  Place,
  Google,
  PhoneIphone,
  PlayArrow,
  StarRounded,
  DeleteOutline
} from '@mui/icons-material';
import AppBarContext from 'context/AppBarContext';
import {
  MY_WEEKLY_ROUTINE,
  PLAY_EXERCISES,
  ADD_ROUTINE_ITEM,
  REMOVE_ROUTINE_ITEM,
  MOVE_ROUTINE_ITEM,
  CLEAR_ROUTINE_DAY
} from 'api/graphql/play';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import playWorkoutSlice, { selectWorkoutActive } from 'store/modules/playWorkout';
import {
  DAY_VALUES,
  dayLabel,
  muscleLabel,
  locationLabel,
  todayIso,
  primaryMuscleOf,
  dayLocationOf,
  muscleDistributionOf
} from 'components/play/constants';
import { buildWorkoutPlan } from 'components/play/workoutEstimate';
import BodyMap from 'components/play/BodyMap';
import MuscleFocusList from 'components/play/MuscleFocusList';
import RoutineDayTabs from 'components/play/RoutineDayTabs';
import RecommendedExerciseRow from 'components/play/RecommendedExerciseRow';
import PlayExerciseDetailModal from 'components/play/PlayExerciseDetailModal';
import PublishRoutineDialog from 'components/play/PublishRoutineDialog';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import { buildRoutineIcs, downloadIcs, buildGoogleCalendarUrls, nextWeekday } from 'components/play/routineIcs';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import routes from 'common/routes';

/**
 * OTRIX Play — Mi rutina. A la izquierda la distribución muscular de la semana (mapa
 * corporal + niveles de enfoque); a la derecha la semana como tira de días, y del día
 * elegido: lo que ya tienes (mover / quitar) y los recomendados del catálogo para ese
 * músculo (agregar en un toque). Al pie, arrancar el motor de ejecución.
 */
const MIN_ITEMS_PER_DAY = 2;
const MAX_SUGGESTIONS = 6;

function WeeklyRoutineBuilder() {
  const { t } = useTranslation(['play']);
  const { setAppBarTitle } = useContext(AppBarContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const workoutActive = useSelector(selectWorkoutActive);

  const iso = todayIso();
  const [selectedDay, setSelectedDay] = useState(iso);
  // Músculo elegido a mano (lista de enfoque / mapa / chips): manda sobre el del día.
  const [muscleOverride, setMuscleOverride] = useState(null);
  const [moveAnchor, setMoveAnchor] = useState(null);
  const [moveItemId, setMoveItemId] = useState(null);
  const [calAnchor, setCalAnchor] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  // Confirmaciones destructivas (regla del proyecto: vaciar/eliminar SIEMPRE con confirmación).
  const [confirmClear, setConfirmClear] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);

  useEffect(() => {
    setAppBarTitle(t('routine.title', { ns: 'play' }));
  }, [setAppBarTitle, t]);

  const { data, loading } = useQuery(MY_WEEKLY_ROUTINE, { fetchPolicy: 'cache-and-network' });
  const refetch = [{ query: MY_WEEKLY_ROUTINE }];

  const [addItem] = useMutation(ADD_ROUTINE_ITEM, { refetchQueries: refetch });
  const [removeItem] = useMutation(REMOVE_ROUTINE_ITEM, { refetchQueries: refetch });
  const [moveItem] = useMutation(MOVE_ROUTINE_ITEM, { refetchQueries: refetch });
  const [clearDay] = useMutation(CLEAR_ROUTINE_DAY, { refetchQueries: refetch });

  const routine = data?.myWeeklyRoutine;
  /**
   * Se descartan los items huérfanos (`exercise` en null).
   *
   * `Exercise` es soft-delete y `weekly_routine_items` es FK-free (convención del repo), así que
   * al borrar un ejercicio del catálogo su item de rutina sobrevive con el `exercise_id` intacto
   * pero la relación ya no resuelve. Se filtra aquí, en la entrada, para que ninguna vista del día
   * tenga que defenderse por su cuenta.
   */
  const items = useMemo(() => (routine?.items || []).filter((i) => i.exercise), [routine]);

  const dayItems = useMemo(
    () => items.filter((i) => i.day_of_week === selectedDay).sort((a, b) => (a.position || 0) - (b.position || 0)),
    [items, selectedDay]
  );

  const countsByDay = useMemo(
    () =>
      items.reduce((acc, i) => {
        acc[i.day_of_week] = (acc[i.day_of_week] || 0) + 1;
        return acc;
      }, {}),
    [items]
  );

  // Distribución muscular de la semana (rutina) -> niveles 1..3 para el mapa y la lista.
  const distribution = useMemo(() => muscleDistributionOf(items), [items]);

  const topMuscle = useMemo(() => {
    const rows = Object.entries(distribution).sort((a, b) => b[1] - a[1]);
    return rows.length > 0 ? rows[0][0] : null;
  }, [distribution]);

  // Músculo con menos estímulo de la semana: lo que proponemos en un día de descanso.
  const deficitMuscle = useMemo(() => {
    const rows = Object.entries(distribution).sort((a, b) => a[1] - b[1]);
    return rows.length > 0 ? rows[0][0] : null;
  }, [distribution]);

  const dayMuscle = useMemo(() => primaryMuscleOf(dayItems), [dayItems]);
  const dayLocation = useMemo(() => dayLocationOf(dayItems), [dayItems]);
  const suggestMuscle = muscleOverride || dayMuscle || deficitMuscle;

  const {
    data: suggestData,
    loading: suggestLoading,
    error: suggestError
  } = useQuery(PLAY_EXERCISES, {
    variables: { muscle_group: suggestMuscle },
    skip: !suggestMuscle
  });

  // Recomendados = catálogo del músculo objetivo menos lo que ya está en ese día.
  const suggestions = useMemo(() => {
    const inDay = new Set(dayItems.map((i) => i.exercise_id));
    return (suggestData?.playExercises || []).filter((e) => !inDay.has(e.id)).slice(0, MAX_SUGGESTIONS);
  }, [suggestData, dayItems]);

  const toast = (message, variant = 'success') =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `rt-${Date.now()}`, variant, message })));

  const handleSelectDay = (day) => {
    setSelectedDay(day);
    // El músculo vuelve a seguir al del día al cambiar de pestaña.
    setMuscleOverride(null);
  };

  const handleAdd = async (exercise) => {
    const sets = parseInt(exercise.suggested_sets, 10);
    try {
      await addItem({
        variables: {
          exercise_id: exercise.id,
          day_of_week: selectedDay,
          target_sets: Number.isNaN(sets) ? null : sets,
          target_reps: exercise.suggested_reps || null,
          notes: null
        }
      });
      toast(t('routine.added', { ns: 'play', name: exercise.localized_name, day: dayLabel(t, selectedDay) }));
    } catch {
      toast(t('routine.errorAdd', { ns: 'play' }), 'error');
    }
  };

  const handleRemove = async () => {
    if (!pendingRemove) return;
    try {
      await removeItem({ variables: { id: pendingRemove } });
      toast(t('routine.removed', { ns: 'play' }));
    } catch {
      toast(t('routine.errorRemove', { ns: 'play' }), 'error');
    } finally {
      setPendingRemove(null);
    }
  };

  const handleMove = async (day) => {
    try {
      await moveItem({ variables: { id: moveItemId, day_of_week: day } });
      toast(t('routine.moved', { ns: 'play' }));
    } catch {
      toast(t('routine.errorMove', { ns: 'play' }), 'error');
    } finally {
      setMoveAnchor(null);
      setMoveItemId(null);
    }
  };

  const handleClearDay = async () => {
    try {
      await clearDay({ variables: { day_of_week: selectedDay } });
      toast(t('routine.dayCleared', { ns: 'play' }));
    } catch {
      toast(t('routine.errorClear', { ns: 'play' }), 'error');
    } finally {
      setConfirmClear(false);
    }
  };

  // Descarga el .ics (multi-evento) para agregar toda la semana de una en Apple/dispositivo.
  const exportDeviceIcs = () => {
    const ics = buildRoutineIcs(routine, (day) => dayLabel(t, day));
    downloadIcs(ics);
    toast(t('routine.calendarDownloaded', { ns: 'play' }));
    setCalAnchor(null);
  };

  // Abre Google Calendar directo (una pestaña por día de entreno), sin subir/descargar .ics.
  const openGoogleCalendar = () => {
    const urls = buildGoogleCalendarUrls(routine, (day) => dayLabel(t, day));
    setCalAnchor(null);
    if (urls.length === 0) {
      toast(t('routine.calendarEmpty', { ns: 'play' }), 'error');
      return;
    }
    urls.forEach((url) => window.open(url, '_blank', 'noopener'));
    toast(t('routine.calendarGoogleOpened', { ns: 'play', count: urls.length }));
  };

  // Lleva a la Agenda posicionada en el día elegido, que llega parpadeando en dorado
  // (deep-link `?date=`). El día ISO se traduce a su PRÓXIMA fecha real: la Agenda arranca la
  // semana en domingo, así que "el domingo de esta semana" ya habría pasado.
  const goToAgenda = () => {
    setCalAnchor(null);
    const date = format(nextWeekday(selectedDay), 'yyyy-MM-dd');
    navigate(`${routes.calendar.path}?date=${date}`);
  };

  // Arranca el motor de ejecución para el día elegido (no pisa una sesión ya en curso).
  const handleStart = () => {
    if (!workoutActive) {
      dispatch(
        playWorkoutSlice.actions.start({
          dayOfWeek: selectedDay,
          muscleFocus: dayMuscle,
          // La progresión del mesociclo se aplica al ARRANCAR: el builder muestra/edita la base.
          exercises: buildWorkoutPlan(dayItems, routine?.progression_week || 1),
          now: Date.now(),
          progressionWeek: routine?.progression_week || null
        })
      );
    }
    navigate(routes.playWorkout.path);
  };

  if (loading && !routine) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress color="secondary" />
      </Stack>
    );
  }

  const hasWeek = items.length > 0;

  const dayHeading = dayMuscle
    ? `${dayLabel(t, selectedDay)} · ${muscleLabel(t, dayMuscle)}`
    : dayLabel(t, selectedDay);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {routine?.name || t('routine.defaultName', { ns: 'play' })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('routine.shareWeekHint', { ns: 'play' })}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button startIcon={<EventAvailable />} variant="outlined" onClick={(e) => setCalAnchor(e.currentTarget)}>
            {t('routine.addToCalendar', { ns: 'play' })}
          </Button>
          <Button startIcon={<Publish />} variant="outlined" disabled={!hasWeek} onClick={() => setPublishOpen(true)}>
            {t('routine.publishToForum', { ns: 'play' })}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3} alignItems="flex-start">
        {/* IZQUIERDA — distribución muscular de la semana */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              p: { xs: 2, md: 2.5 },
              border: '1px solid',
              borderColor: 'hairline.main',
              borderRadius: '16px',
              bgcolor: 'surface.content',
              position: { md: 'sticky' },
              top: { md: 16 }
            }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>
              {t('routine.distributionTitle', { ns: 'play' })}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {t('routine.distributionHint', { ns: 'play' })}
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
              <Box sx={{ width: '100%', flex: { md: '1 1 55%' }, minWidth: 0 }}>
                <BodyMap distribution={hasWeek ? distribution : null} selected={suggestMuscle} onSelect={setMuscleOverride} />
              </Box>

              {hasWeek && (
                <Box sx={{ width: '100%', flex: { md: '1 1 45%' }, minWidth: 0 }}>
                  <MuscleFocusList distribution={distribution} selected={suggestMuscle} onSelect={setMuscleOverride} />
                </Box>
              )}
            </Stack>

            {hasWeek && topMuscle && (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'gold.soft', border: '1px solid', borderColor: 'hairline.main' }}>
                <StarRounded sx={{ color: 'gold.main', fontSize: 20, flex: 'none' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={800} sx={{ color: 'gold.main' }}>
                    {t('routine.mainFocus', { ns: 'play' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('routine.mainFocusHint', { ns: 'play', muscle: muscleLabel(t, topMuscle) })}
                  </Typography>
                </Box>
              </Stack>
            )}

            {!hasWeek && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
                {t('routine.noDistribution', { ns: 'play' })}
              </Typography>
            )}
          </Card>
        </Grid>

        {/* DERECHA — la semana y el día elegido */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: { xs: 2, md: 2.5 },
              border: '1px solid',
              borderColor: 'hairline.main',
              borderRadius: '16px',
              bgcolor: 'surface.content'
            }}>
            <RoutineDayTabs
              selectedDay={selectedDay}
              todayDay={iso}
              countsByDay={countsByDay}
              minItems={MIN_ITEMS_PER_DAY}
              onSelect={handleSelectDay}
            />

            <Divider sx={{ borderColor: 'hairline.main', my: 2 }} />

            {/* Cabecera del día */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ textTransform: 'capitalize' }}>
                {dayHeading}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                {dayLocation && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<Place sx={{ fontSize: 14 }} />}
                    label={locationLabel(t, dayLocation)}
                    sx={{ height: 22 }}
                  />
                )}
                {dayItems.length > 0 && dayItems.length < MIN_ITEMS_PER_DAY && (
                  <Chip
                    size="small"
                    label={t('routine.incomplete', { ns: 'play' })}
                    sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 22 }}
                  />
                )}
              </Stack>
            </Stack>

            {/* En tu rutina */}
            {dayItems.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {t('routine.inYourRoutine', { ns: 'play' })}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                    onClick={() => setConfirmClear(true)}
                    sx={{ color: 'sessionStatus.absent.fg', textTransform: 'none', fontWeight: 700, minHeight: 0 }}>
                    {t('routine.clearDay', { ns: 'play' })}
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {dayItems.map((item, idx) => (
                    <RecommendedExerciseRow
                      key={item.id}
                      index={idx + 1}
                      exercise={item.exercise}
                      sets={item.target_sets}
                      reps={item.target_reps}
                      mode="routine"
                      onViewTechnique={setDetailTarget}
                      onMove={(e) => {
                        setMoveAnchor(e.currentTarget);
                        setMoveItemId(item.id);
                      }}
                      onRemove={() => setPendingRemove(item.id)}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'hairline.strong',
                  bgcolor: 'surface.overlay'
                }}>
                <Typography variant="body2" fontWeight={800} sx={{ mb: 0.5 }}>
                  {t('routine.rest', { ns: 'play' })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {suggestMuscle
                    ? t('routine.restHint', { ns: 'play', muscle: muscleLabel(t, suggestMuscle) })
                    : t('routine.empty', { ns: 'play' })}
                </Typography>
              </Box>
            )}

            {/* Recomendados para agregar */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" fontWeight={800} sx={{ color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {t('routine.recommended', { ns: 'play' })}
              </Typography>
              {suggestMuscle && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, mb: 1 }}>
                  {t('routine.recommendedHint', { ns: 'play', muscle: muscleLabel(t, suggestMuscle) })}
                </Typography>
              )}

              {(() => {
                if (suggestLoading) {
                  return (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {[0, 1, 2].map((k) => (
                        <Skeleton key={k} variant="rounded" height={90} sx={{ borderRadius: '14px' }} />
                      ))}
                    </Stack>
                  );
                }
                if (suggestError) {
                  return (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {t('routine.recommendedError', { ns: 'play' })}
                    </Typography>
                  );
                }
                if (suggestions.length === 0) {
                  return (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {t('routine.recommendedEmpty', { ns: 'play' })}
                    </Typography>
                  );
                }
                return (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {suggestions.map((ex, idx) => (
                      <RecommendedExerciseRow
                        key={ex.id}
                        index={dayItems.length + idx + 1}
                        exercise={ex}
                        sets={ex.suggested_sets}
                        reps={ex.suggested_reps}
                        mode="suggestion"
                        onViewTechnique={setDetailTarget}
                        onAdd={handleAdd}
                      />
                    ))}
                  </Stack>
                );
              })()}

              <Button
                size="small"
                startIcon={<AddCircleOutline />}
                onClick={() => navigate(`${routes.playExercises.path}?muscle=${suggestMuscle || ''}`)}
                sx={{ mt: 1.5, color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
                {t('routine.viewCatalog', { ns: 'play' })}
              </Button>
            </Box>

            {/* CTAs del día */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 3 }}>
              <Button
                startIcon={<PlayArrow />}
                disabled={dayItems.length === 0}
                onClick={handleStart}
                sx={{ ...premiumButtonSx, flex: 1 }}>
                {t('routine.startRoutine', { ns: 'play' })}
              </Button>
              {/* Va DIRECTO a la Agenda con el día seleccionado; el menú de "Agregar a mi
                  calendario" es otra cosa (calendarios externos). */}
              <Button startIcon={<EventAvailable />} variant="outlined" onClick={goToAgenda}>
                {t('routine.viewCalendar', { ns: 'play' })}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Mover a otro día */}
      <Menu anchorEl={moveAnchor} open={Boolean(moveAnchor)} onClose={() => setMoveAnchor(null)}>
        {DAY_VALUES.filter((day) => day !== selectedDay).map((day) => (
          <MenuItem key={day} onClick={() => handleMove(day)} sx={{ textTransform: 'capitalize' }}>
            {dayLabel(t, day)}
          </MenuItem>
        ))}
      </Menu>

      {/* Agregar al calendario */}
      <Menu anchorEl={calAnchor} open={Boolean(calAnchor)} onClose={() => setCalAnchor(null)}>
        <MenuItem onClick={openGoogleCalendar}>
          <Google fontSize="small" sx={{ mr: 1 }} />
          {t('routine.calGoogle', { ns: 'play' })}
        </MenuItem>
        <MenuItem onClick={exportDeviceIcs}>
          <PhoneIphone fontSize="small" sx={{ mr: 1 }} />
          {t('routine.calDevice', { ns: 'play' })}
        </MenuItem>
      </Menu>

      <PlayExerciseDetailModal
        exercise={detailTarget}
        open={Boolean(detailTarget)}
        onClose={() => setDetailTarget(null)}
        onAdd={handleAdd}
        alreadyAdded={Boolean(detailTarget && dayItems.some((i) => i.exercise_id === detailTarget.id))}
      />

      <PublishRoutineDialog open={publishOpen} onClose={() => setPublishOpen(false)} />

      {/* Vaciar día — confirmación destructiva */}
      <ConfirmDeleteModal
        open={confirmClear}
        title={t('routine.clearConfirmTitle', { ns: 'play', day: dayLabel(t, selectedDay) })}
        message={t('routine.clearConfirmBody', { ns: 'play' })}
        confirmLabel={t('routine.clearDay', { ns: 'play' })}
        onConfirm={handleClearDay}
        onClose={() => setConfirmClear(false)}
      />

      {/* Quitar ejercicio — confirmación destructiva */}
      <ConfirmDeleteModal
        open={Boolean(pendingRemove)}
        title={t('routine.removeConfirmTitle', { ns: 'play' })}
        message={t('routine.removeConfirmBody', { ns: 'play' })}
        confirmLabel={t('routine.removeConfirmCta', { ns: 'play' })}
        onConfirm={handleRemove}
        onClose={() => setPendingRemove(null)}
      />
    </Box>
  );
}

export default WeeklyRoutineBuilder;
